import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Queue } from "@/types/queue";
import { AlertCircle, RotateCcw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EditQueueDialogProps {
  queue: Queue | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditQueue: (queue: Queue) => void;
}

export function EditQueueDialog({ queue, open, onOpenChange, onEditQueue }: EditQueueDialogProps) {
  const [name, setName] = useState("");
  const [showResetWarning, setShowResetWarning] = useState(false);

  useEffect(() => {
    if (queue) {
      setName(queue.name);
      setShowResetWarning(false);
    }
  }, [queue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (queue) {
      onEditQueue({
        ...queue,
        name,
      });
      onOpenChange(false);
    }
  };

  const handleReset = () => {
    if (queue) {
      onEditQueue({
        ...queue,
        current_number: 0,
        people_in_queue: 0,
        last_updated: new Date().toISOString(),
      });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Queue</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Queue Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter queue name"
              required
            />
          </div>
          
          {!showResetWarning ? (
            <Button 
              type="button" 
              variant="outline" 
              className="w-full"
              onClick={() => setShowResetWarning(true)}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset Queue Number
            </Button>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                Are you sure? This will reset the queue to 0.
                <div className="space-x-2 mt-2">
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={handleReset}
                  >
                    Yes, reset
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowResetWarning(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <Button type="submit" className="w-full">Save Changes</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}